import React from "react";
import { Phone, Mail, ExternalLink, GraduationCap, HandHeart, Award, Users } from "lucide-react";
import { PageHero } from "@/components/Bits";
import { Reveal } from "@/components/Reveal";
import { TRAINING_GROUPS } from "@/lib/training";
import { IMG } from "@/lib/images";

const ICONS = { learn: GraduationCap, serve: HandHeart, advanced: Award };

const OrgCard = ({ o, n }) => (
  <div className="h-full bg-white rounded-2xl p-6 sm:p-7 card-tactile flex flex-col" data-testid={`training-org-${n}`}>
    <div className="flex items-start gap-3">
      <span className="w-9 h-9 rounded-full bg-bsn-midnight text-bsn-gold font-display font-semibold flex items-center justify-center shrink-0">{n}</span>
      <h3 className="font-serif text-xl font-semibold text-bsn-midnight leading-snug pt-1">{o.name}</h3>
    </div>
    <p className="mt-3 font-serif italic text-bsn-bronze leading-snug">{o.tagline}</p>
    <p className="mt-3 text-sm text-bsn-sapphire/80 leading-relaxed flex-1">{o.text}</p>
    <div className="mt-5 space-y-2 text-sm">
      <p className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${o.free ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-bsn-canvas text-bsn-sapphire border border-bsn-gold/30"}`}>{o.cost}</p>
      <p className="text-bsn-sapphire/75">
        <span className="font-semibold text-bsn-midnight">{o.contact.kind}: </span>
        {o.contact.href ? (
          <a href={o.contact.href} target={o.contact.href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className="inline-flex items-center gap-1 text-bsn-bronze hover:underline">
            {o.contact.href.startsWith("tel") ? <Phone className="w-3.5 h-3.5" /> : o.contact.href.startsWith("mailto") ? <Mail className="w-3.5 h-3.5" /> : null}{o.contact.label}
          </a>
        ) : o.contact.label}
      </p>
    </div>
    <div className="mt-5 flex flex-wrap gap-2">
      {o.links.map((l) => (
        <a key={l.href} href={l.href} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full btn-gold text-sm font-semibold">
          {l.label} <ExternalLink className="w-3.5 h-3.5" />
        </a>
      ))}
    </div>
  </div>
);

export default function Training() {
  let n = 0;
  return (
    <div data-testid="training-page">
      <PageHero
        eyebrow="Training & Volunteering"
        title="Be someone who knows how to help"
        subtitle="Learn the skills. Offer support. Help someone find hope."
        image={IMG.caringHands}
        testid="training-hero"
      />

      <section className="max-w-3xl mx-auto px-4 pt-14 text-center">
        <p className="text-lg text-bsn-sapphire/85 leading-relaxed">When someone is struggling, knowing what to say can make it easier to reach out. Training can help you recognize warning signs, listen with care, and connect someone with professional support.</p>
        <p className="mt-3 text-bsn-sapphire/70">Explore these organizations to find a course or volunteer opportunity that fits your interests and schedule. The contacts listed are for training, volunteering, or general inquiries.</p>
        <nav className="mt-8 flex flex-wrap justify-center gap-3" aria-label="Jump to section" data-testid="training-jump-nav">
          {TRAINING_GROUPS.map((g) => {
            const Icon = ICONS[g.id];
            return (
              <a key={g.id} href={`#${g.id}`} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-bsn-midnight text-bsn-parchment text-sm font-semibold border border-bsn-gold/40 hover:border-bsn-gold" data-testid={`training-jump-${g.id}`}>
                <Icon className="w-4 h-4 text-bsn-gold" /> {g.title}
              </a>
            );
          })}
        </nav>
      </section>

      {TRAINING_GROUPS.map((g, gi) => {
        const Icon = ICONS[g.id];
        return (
          <section key={g.id} id={g.id} className={`scroll-mt-24 ${gi % 2 ? "bg-bsn-canvas border-y border-bsn-gold/20" : ""}`} data-testid={`training-group-${g.id}`}>
            <div className="max-w-6xl mx-auto px-4 py-14 sm:py-16">
              <div className="flex items-center gap-4 mb-2">
                <span className="w-12 h-12 rounded-xl bg-bsn-gold/15 flex items-center justify-center"><Icon className="w-6 h-6 text-bsn-bronze" /></span>
                <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-bsn-midnight">{g.title}</h2>
              </div>
              <p className="text-bsn-sapphire/75 mb-8 max-w-2xl">{g.blurb}</p>
              <div className="grid md:grid-cols-2 gap-6">
                {g.orgs.map((o) => { n += 1; return <Reveal key={o.name}><OrgCard o={o} n={n} /></Reveal>; })}
              </div>
            </div>
          </section>
        );
      })}

      <section className="bg-bsn-midnight text-bsn-parchment">
        <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="training-first-step">
          <Users className="w-9 h-9 text-bsn-gold mx-auto mb-4" />
          <h2 className="font-serif text-3xl sm:text-4xl font-semibold">Take the First Step</h2>
          <p className="mt-5 text-bsn-ice/85 leading-relaxed">Choose a course that fits your goals. Explore a volunteer role that fits your life. Invite a friend, coworker, or member of your faith community to learn alongside you.</p>
          <p className="mt-4 font-serif text-xl text-bsn-champagne italic">You do not have to carry someone's crisis alone. Learning how to connect them with help is a meaningful place to begin.</p>
          <div className="mt-10 text-xs text-bsn-ice/55 space-y-2 border-t border-bsn-gold/20 pt-6">
            <p>Training builds support skills. It does not replace professional licensure or qualify someone to provide independent mental health treatment.</p>
            <p>Fees, eligibility, and openings can change. Confirm details directly with each organization. Training fees are costs to attend a course, not payment to participants.</p>
            <p>Information checked October 31, 2026.</p>
          </div>
        </div>
      </section>
    </div>
  );
}
